import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { AlertTriangle, TrendingDown, Code, Lightbulb } from 'lucide-react';
import './ErrorColumnAnalysis.css';

function ErrorColumnAnalysis({ data, errorAnalysisData, analysisType }) {

  // Helper function to determine severity based on failed count
  const getSeverity = (failed, total) => {
    const failureRate = (failed / total) * 100;
    if (failureRate === 0) return 5; // No errors
    if (failureRate < 5) return 4;   // Low
    if (failureRate < 15) return 3;  // Medium
    if (failureRate < 30) return 2;  // High
    return 1;                        // Critical
  };

  const severityColors = {
    1: '#dc2626', // Critical - Red
    2: '#f97316', // High - Orange
    3: '#fbbf24', // Medium - Yellow
    4: '#22c55e', // Low - Green
    5: '#10b981'  // No errors - Light Green
  };

  const severityLabels = {
    1: 'Critical (≥30%)',
    2: 'High (15-30%)',
    3: 'Medium (5-15%)',
    4: 'Low (<5%)',
    5: 'No Errors'
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      const dimension = data.dimension || data.label;
      const columns = data.columns || [];
      
      return (
        <div className="custom-tooltip-severity bg-white p-4 rounded-lg shadow-xl border-2 border-gray-200">
          <p className="tooltip-title font-bold text-gray-900 mb-2">{dimension}</p>
          {columns.length > 0 ? (
            columns.map((col, idx) => (
              <p key={idx} className="tooltip-row text-xs text-gray-600 mb-1">
                <span className="tooltip-dot inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: severityColors[col.severity] }}></span>
                {col.column_name || col.name}: {(col.failed || 0).toLocaleString()} / {(col.total || 0).toLocaleString()} failed
              </p>
            ))
          ) : (
            <p className="text-xs text-gray-500">No column details available</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Multi-file severity-based bar chart
  if (analysisType === 'multi_file' && errorAnalysisData && errorAnalysisData.length > 0) {
    // Group data by dimension instead of by individual column
    const dimensionMap = {};
    
    // Check if errorAnalysisData is array of dimensions with columns property
    const isStructuredData = errorAnalysisData.some(item => item.columns && Array.isArray(item.columns));
    
    if (isStructuredData) {
      // Data structure: [{ dimension, columns: [...] }, ...]
      errorAnalysisData.forEach(dimData => {
        const dimension = dimData.dimension;
        
        if (!dimensionMap[dimension]) {
          dimensionMap[dimension] = {
            dimension: dimension,
            columns: [],
            severity_1: 0,
            severity_2: 0,
            severity_3: 0,
            severity_4: 0
          };
        }
        
        if (dimData.columns && Array.isArray(dimData.columns)) {
          dimData.columns.forEach(col => {
            const total = col.total_records || col.total;
            const severity = getSeverity(col.failed, total);
            
            // Store column details for tooltip (always store)
            dimensionMap[dimension].columns.push({
              column_name: col.column,
              failed: col.failed,
              total: total,
              severity: severity,
              filename: col.filename
            });
            
            // Aggregate severity counts (only severity 1-4)
            if (severity >= 1 && severity <= 4) {
              const severityKey = `severity_${severity}`;
              dimensionMap[dimension][severityKey] += col.failed;
            }
          });
        }
      });
    } else {
      // Data structure: flat array of records with dimension and column properties
      errorAnalysisData.forEach(record => {
        const dimension = record.dimension;
        
        if (!dimensionMap[dimension]) {
          dimensionMap[dimension] = {
            dimension: dimension,
            columns: [],
            severity_1: 0,
            severity_2: 0,
            severity_3: 0,
            severity_4: 0
          };
        }
        
        const severity = getSeverity(record.failed, record.total_records);
        
        // Store column details for tooltip (always store)
        dimensionMap[dimension].columns.push({
          column_name: record.column,
          failed: record.failed,
          total: record.total_records,
          severity: severity,
          filename: record.filename
        });
        
        // Aggregate severity counts (only severity 1-4)
        if (severity >= 1 && severity <= 4) {
          const severityKey = `severity_${severity}`;
          dimensionMap[dimension][severityKey] += record.failed;
        }
      });
    }
    
    // Convert to array for chart - include all dimensions with at least one column
    const multiFileChartData = Object.values(dimensionMap).filter(item => 
      item.columns && item.columns.length > 0
    );


    const multiFileDimensionData = Object.values(dimensionMap)
  .filter(d => d.columns.length > 0)
  .reduce((acc, dim) => {
    acc[dim.dimension] = {
      dimension: dim.dimension,
      columns: dim.columns
    };
    return acc;
  }, {});


    console.log('Multi-file chart data:', multiFileChartData);
    console.log('Original errorAnalysisData:', errorAnalysisData);
    console.log('dimensionMap:', dimensionMap);

    return (
      <div className="error-column-analysis">
        <h3 className="section-title text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <span className="text-3xl">📊</span>
          Failed Columns Analysis by Dimension
        </h3>
        
        <div className="stacked-chart-container bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
          {multiFileChartData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No data available for visualization</p>
              <p className="text-xs text-gray-400 mt-2">errorAnalysisData length: {errorAnalysisData?.length || 0}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(400, multiFileChartData.length * 100)}>
              <BarChart
                data={multiFileChartData}
                layout="vertical"
                margin={{ top: 20, right: 30, left: 180, bottom: 20 }}
              >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis 
                type="number" 
                stroke="#64748b"
                label={{ value: 'Failed Records', position: 'insideBottom', offset: -10, style: { fill: '#64748b', fontWeight: 600 } }}
              />
              <YAxis
                dataKey="dimension"
                type="category"
                stroke="#64748b"
                width={140}
                tick={{ fontSize: 13, fontWeight: 700 }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              
              {[1, 2, 3, 4].map(severity => (
                <Bar
                  key={severity}
                  dataKey={`severity_${severity}`}
                  stackId="severity-stack"
                  fill={severityColors[severity]}
                  name={severityLabels[severity]}
                  radius={[0, 8, 8, 0]}
                  isAnimationActive={true}
                  animationDuration={300}
                />
              ))}
              </BarChart>
            </ResponsiveContainer>
          )}
          
          <div className="severity-legend mt-8 p-4 bg-gray-50 rounded-xl">
            <h4 className="font-bold text-gray-900 mb-4">Severity Levels</h4>
            <div className="legend-items grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(severity => (
                <div key={severity} className="legend-item flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="legend-color w-4 h-4 rounded" style={{ backgroundColor: severityColors[severity] }}></div>
                  <span className="text-xs font-semibold text-gray-700">{severityLabels[severity]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Detailed Column Failures (Multi-file) */}
<div className="mt-8">
  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
    <AlertTriangle className="w-5 h-5 text-red-500" />
    Detailed Column Failures (All Files)
  </h4>

  <div className="space-y-4">
    {Object.keys(multiFileDimensionData).map(dimension => (
      <div key={dimension} className="bg-white rounded-xl border-2 border-gray-100 overflow-hidden shadow-md">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b-2 border-gray-100">
          <h5 className="font-bold text-gray-900">{dimension}</h5>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-bold">File</th>
                <th className="px-4 py-3 text-left text-xs font-bold">Column</th>
                <th className="px-4 py-3 text-center text-xs font-bold">Failed</th>
                <th className="px-4 py-3 text-center text-xs font-bold">Total</th>
                <th className="px-4 py-3 text-center text-xs font-bold">Severity</th>
              </tr>
            </thead>
            <tbody>
              {multiFileDimensionData[dimension].columns.map((col, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {col.filename || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs font-semibold">
                    {col.column_name}
                  </td>
                  <td className="px-4 py-3 text-center text-xs font-bold text-red-600">
                    {col.failed.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-center text-xs">
                    {col.total.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: severityColors[col.severity] }}
                    >
                      {severityLabels[col.severity]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ))}
  </div>
</div>

      </div>
    );
  }

  // Single file severity-based bar chart with detailed column analysis
  if (!data || !data.columns || data.columns.length === 0) {
    return null;
  }

  // Group columns by dimension with severity
  const dimensionData = {};
  data.columns.forEach(col => {
    if (!dimensionData[col.dimension]) {
      dimensionData[col.dimension] = {
        dimension: col.dimension,
        columns: []
      };
    }
    
    if (col.failed > 0) {
      const severity = getSeverity(col.failed, col.total);
      dimensionData[col.dimension].columns.push({
        column_name: col.column,
        failed: col.failed,
        total: col.total,
        severity: severity
      });
    }
  });

  // Prepare stacked bar chart data by severity
  const chartData = Object.keys(dimensionData).map(dimension => {
    const row = { dimension };
    
    dimensionData[dimension].columns.forEach(col => {
      const severityKey = `severity_${col.severity}`;
      row[severityKey] = (row[severityKey] || 0) + col.failed;
    });
    
    row.columns = dimensionData[dimension].columns;
    return row;
  });

  return (
    <div className="error-column-analysis">
      <h3 className="section-title text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <span className="text-3xl">📊</span>
        Failed Columns Analysis by Dimension
      </h3>
      
      <div className="stacked-chart-container bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100">
        <ResponsiveContainer width="100%" height={Math.max(300, Object.keys(dimensionData).length * 120)}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis 
              type="number" 
              stroke="#64748b"
              label={{ value: 'Failed Records', position: 'insideBottom', offset: -10, style: { fill: '#64748b', fontWeight: 600 } }}
            />
            <YAxis
              dataKey="dimension"
              type="category"
              stroke="#64748b"
              width={140}
              tick={{ fontSize: 13, fontWeight: 700 }}
            />
            <Tooltip content={<CustomTooltip />} cursor={false} />
            
            {[1, 2, 3, 4].map(severity => (
              <Bar
                key={severity}
                dataKey={`severity_${severity}`}
                stackId="a"
                fill={severityColors[severity]}
                name={severityLabels[severity]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
        
        <div className="severity-legend mt-8 p-4 bg-gray-50 rounded-xl">
          <h4 className="font-bold text-gray-900 mb-4">Severity Levels</h4>
          <div className="legend-items grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(severity => (
              <div key={severity} className="legend-item flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                <div className="legend-color w-4 h-4 rounded" style={{ backgroundColor: severityColors[severity] }}></div>
                <span className="text-xs font-semibold text-gray-700">{severityLabels[severity]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Column Failures */}
      <div className="mt-8">
        <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          Detailed Column Failures
        </h4>
        
        <div className="space-y-4">
          {Object.keys(dimensionData).map(dimension => (
            <div key={dimension} className="bg-white rounded-xl border-2 border-gray-100 overflow-hidden shadow-md">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b-2 border-gray-100">
                <h5 className="font-bold text-gray-900">{dimension}</h5>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700">Column</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700">Failed</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700">Failure %</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dimensionData[dimension].columns.map((col, idx) => {
                      const failureRate = (col.failed / col.total * 100).toFixed(2);
                      return (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-xs font-semibold text-gray-900">{col.column_name}</td>
                          <td className="px-4 py-3 text-center text-xs font-bold text-red-600">{col.failed.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center text-xs text-gray-600">{col.total.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-block px-2 py-1 rounded text-xs font-bold" 
                              style={{
                                backgroundColor: `${severityColors[col.severity]}20`,
                                color: severityColors[col.severity]
                              }}>
                              {failureRate}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: severityColors[col.severity] }}>
                              {severityLabels[col.severity]}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
      
    </div>

    
  );
}

export default ErrorColumnAnalysis;
